import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import ProductMentionEditor from '../components/ProductMentionEditor';
import ProductMentionRenderer from '../components/ProductMentionRenderer';
import './ForumPage.css';

const categories = [
  {
    title: 'Willkommen & Community',
    icon: '👋',
    description: 'Ankommen, Regeln, Vorstellungen und Off-Topic für die Community.',
    topics: [
      'Vorstellungsrunde & Community-News',
      'Forenregeln und Moderation',
      'Ankündigungen & Updates',
      'Off-Topic Lounge'
    ]
  },
  {
    title: 'Anbau-Grundlagen',
    icon: '🌱',
    description: 'Der Start in den Grow: von Keimung bis Ernte mit Tipps für jede Phase.',
    topics: [
      'Indoor vs. Outdoor Wahl',
      'Keimung und Sämlinge',
      'Vegetative Phase & Training',
      'Blütephase, Ernte, Trocknung'
    ]
  },
  {
    title: 'Grow-Equipment & Technik',
    icon: '🔧',
    description: 'Technik-Hub für Beleuchtung, Zelte, Klima, Bewässerung und Automatisierung.',
    topics: [
      'Beleuchtung (LED, HPS, CMH)',
      'Zelte & Growboxen',
      'Belüftung & Klimasteuerung',
      'Bewässerungssysteme & Automatisierung'
    ]
  },
  {
    title: 'Substrate & Nährstoffe',
    icon: '🧪',
    description: 'Erde, Kokos, Hydroponik, organische vs. mineralische Düngung und pH-Wert.',
    topics: [
      'Erde, Kokos, Hydro & Soilless',
      'Organische vs. mineralische Düngung',
      'Kompostierung & lebendige Erde',
      'Wassermanagement & pH-Kontrolle'
    ]
  },
  {
    title: 'Genetik & Sorten',
    icon: '🧬',
    description: 'Sortenempfehlungen, Samenbanken, Phänotypen-Jagd, Klonen und Züchtung.',
    topics: [
      'Sortenempfehlungen & Erfahrungsberichte',
      'Samenbanken & Bezugsquellen',
      'Phänotypen-Jagd & Breeding',
      'Klonen & Mutterpflanzen'
    ]
  },
  {
    title: 'Probleme & Schädlinge',
    icon: '🛟',
    description: 'Schnelle Hilfe bei Mängeln, Schädlingsdruck, Krankheiten oder Schimmel.',
    topics: [
      'Mangelerscheinungen & Überdüngung',
      'Schädlingsbekämpfung',
      'Krankheiten & Schimmel',
      'Umweltstress & Notfallhilfe'
    ]
  },
  {
    title: 'Grow-Tagebücher',
    icon: '📓',
    description: 'Indoor- und Outdoor-Journals, dokumentierte Grows und Ernte-Galerien.',
    topics: [
      'Indoor-Grow Journals',
      'Outdoor-Grow Journals',
      'Step-by-Step Dokumentationen',
      'Ernte-Galerie & Feedback'
    ]
  },
  {
    title: 'Verarbeitung & Konsum',
    icon: '🍃',
    description: 'Alles nach der Ernte: Extrakte, Edibles, Vaporizer und Qualitätserhalt.',
    topics: [
      'Extrakte & Konzentrate',
      'Edibles & Kochen',
      'Verdampfer & Zubehör',
      'Lagerung & Qualitätserhalt'
    ]
  },
  {
    title: 'Rechtliches & Sicherheit',
    icon: '⚖️',
    description: 'Gesetze, Eigenbedarf, Anbauvereinigungen, Datenschutz und Anonymität.',
    topics: [
      'Aktuelle Gesetzeslage nach Ländern',
      'Eigenbedarfsregelungen',
      'Anbauvereinigungen & CSCs',
      'Datenschutz & Anonymität'
    ]
  }
];

function ForumPage() {
  const { user, fetchWithAuth, isAdmin } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [newThread, setNewThread] = useState({ title: '', category: 'Willkommen & Community', content: '' });
  const [replyInputs, setReplyInputs] = useState({});
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');
  const [editingThreadId, setEditingThreadId] = useState(null);
  const [editingThreadTitle, setEditingThreadTitle] = useState('');
  const [editingThreadContent, setEditingThreadContent] = useState('');
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editingReplyContent, setEditingReplyContent] = useState('');

  const openCategory = (catTitle) => {
    setSelectedCategory(catTitle);
    const el = document.getElementById('threads');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getCategoryStats = (catTitle) => {
    const catThreads = threads.filter((t) => t.category === catTitle);
    const count = catThreads.length;
    let latestTs = null;
    catThreads.forEach((t) => {
      const times = [new Date(t.createdAt).getTime(), ...t.replies.map((r) => new Date(r.createdAt).getTime())];
      const maxTime = Math.max(...times, 0);
      if (!Number.isFinite(maxTime)) return;
      latestTs = latestTs == null ? maxTime : Math.max(latestTs, maxTime);
    });
    return { count, latest: latestTs ? new Date(latestTs) : null };
  };

  // Helper: Normalize thread data from API
  const normalizeThread = (t) => ({
    id: t._id || t.id,
    title: t.title,
    category: t.category,
    content: t.content,
    author: t.authorName || 'Unbekannt',
    authorId: t.authorId,
    authorProfileImage: t.authorProfileImage || null,
    createdAt: t.createdAt,
    replies: (t.replies || []).map((r) => ({
      id: r._id || r.id,
      author: r.authorName || 'Unbekannt',
      authorId: r.authorId,
      authorProfileImage: r.authorProfileImage || null,
      content: r.content,
      createdAt: r.createdAt
    }))
  });

  const formatRelativeTime = (date) => {
    if (!date) return '–';
    const now = Date.now();
    const diffMs = Math.max(0, now - date.getTime());
    const sec = Math.floor(diffMs / 1000);
    const min = Math.floor(sec / 60);
    const hrs = Math.floor(min / 60);
    const days = Math.floor(hrs / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    if (months > 0) return `${months}m`;
    if (weeks > 0) return `${weeks}w`;
    if (days > 0) return `${days}d`;
    if (hrs > 0) return `${hrs}h`;
    if (min > 0) return `${min}m`;
    return `${sec}s`;
  };

  // Threads aus der API laden (DB-basiert)
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch('/api/forum/threads');
        const data = await res.json();
        const normalized = (data || []).map(normalizeThread);
        setThreads(normalized);
      } catch (e) {
        // Fallback: leer lassen
      } finally {
        setHasLoaded(true);
      }
    };
    load();
  }, []);

  // Keine LocalStorage-Persistenz mehr nötig – DB übernimmt

  const filteredThreads = useMemo(() => {
    if (!selectedCategory) return threads;
    return threads.filter((t) => t.category === selectedCategory);
  }, [threads, selectedCategory]);

  const handleCreateThread = async () => {
    if (!newThread.title.trim() || !newThread.content.trim()) return;
    try {
      setError('');
      const res = await fetchWithAuth('/api/forum/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newThread.title.trim(),
          category: newThread.category,
          content: newThread.content.trim()
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        setError(err.message || 'Beitrag konnte nicht erstellt werden');
        return;
      }
      const t = await res.json();
      setThreads([normalizeThread(t), ...threads]);
      setNewThread({ title: '', category: newThread.category, content: '' });
    } catch (e) {
      setError('Beitrag konnte nicht erstellt werden');
    }
  };

  const handleAddReply = async (threadId) => {
    const text = (replyInputs[threadId] || '').trim();
    if (!text) return;
    try {
      setError('');
      const res = await fetchWithAuth(`/api/forum/threads/${threadId}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: text })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        setError(err.message || 'Antwort konnte nicht gespeichert werden');
        return;
      }
      const updated = await res.json();
      setThreads((prev) => prev.map((t) => (t.id === threadId ? normalizeThread(updated) : t)));
      setReplyInputs((prev) => ({ ...prev, [threadId]: '' }));
    } catch (e) {
      setError('Antwort konnte nicht gespeichert werden');
    }
  };

  const startEditThread = (thread) => {
    setEditingReplyId(null);
    setEditingReplyContent('');
    setEditingThreadId(thread.id);
    setEditingThreadTitle(thread.title || '');
    setEditingThreadContent(thread.content || '');
  };

  const cancelEditThread = () => {
    setEditingThreadId(null);
    setEditingThreadTitle('');
    setEditingThreadContent('');
  };

  const handleSaveThread = async (threadId) => {
    if (!editingThreadTitle.trim() || !editingThreadContent.trim()) return;
    try {
      setError('');
      const res = await fetchWithAuth(`/api/forum/threads/${threadId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingThreadTitle.trim(),
          content: editingThreadContent.trim()
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        setError(err.message || 'Thread konnte nicht gespeichert werden');
        return;
      }
      const updated = await res.json();
      setThreads((prev) => prev.map((t) => (t.id === threadId ? normalizeThread(updated) : t)));
      cancelEditThread();
    } catch (e) {
      setError('Thread konnte nicht gespeichert werden');
    }
  };

  const startEditReply = (reply) => {
    setEditingThreadId(null);
    setEditingThreadTitle('');
    setEditingThreadContent('');
    setEditingReplyId(reply.id);
    setEditingReplyContent(reply.content || '');
  };

  const cancelEditReply = () => {
    setEditingReplyId(null);
    setEditingReplyContent('');
  };

  const handleSaveReply = async (threadId, replyId) => {
    if (!editingReplyContent.trim()) return;
    try {
      setError('');
      const res = await fetchWithAuth(`/api/forum/threads/${threadId}/replies/${replyId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editingReplyContent.trim() })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        setError(err.message || 'Antwort konnte nicht gespeichert werden');
        return;
      }
      const updated = await res.json();
      setThreads((prev) => prev.map((t) => (t.id === threadId ? normalizeThread(updated) : t)));
      cancelEditReply();
    } catch (e) {
      setError('Antwort konnte nicht gespeichert werden');
    }
  };

  const handleDeleteThread = async (threadId) => {
    if (!window.confirm('Thread wirklich löschen?')) return;
    try {
      setError('');
      const res = await fetchWithAuth(`/api/forum/threads/${threadId}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        setError(err.message || 'Thread konnte nicht gelöscht werden');
        return;
      }
      setThreads((prev) => prev.filter((t) => t.id !== threadId));
    } catch (e) {
      setError('Thread konnte nicht gelöscht werden');
    }
  };

  const handleDeleteReply = async (threadId, replyId) => {
    if (!window.confirm('Antwort wirklich löschen?')) return;
    try {
      setError('');
      const res = await fetchWithAuth(`/api/forum/threads/${threadId}/replies/${replyId}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        setError(err.message || 'Antwort konnte nicht gelöscht werden');
        return;
      }
      const updated = await res.json();
      setThreads((prev) => prev.map((t) => (t.id === threadId ? normalizeThread(updated) : t)));
    } catch (e) {
      setError('Antwort konnte nicht gelöscht werden');
    }
  };

  return (
    <div className="forum-page">
      <section className="forum-hero" aria-label="Forum Einstieg">
        <div className="forum-hero-text">
          <p className="forum-kicker">Community Hub</p>
          <h1>💬 Grow-Forum</h1>
          <p className="forum-lead">
            Tausche dich mit anderen über Technik, Anbau und Erfahrungen aus. Finde Antworten, teile dein Wissen
            und bleibe über neue Setups auf dem Laufenden.
          </p>
          <div className="forum-actions">
            <a href="#new-thread" className="forum-btn primary">Neue Diskussion starten</a>
            <a href="#threads" className="forum-btn ghost">Zu den Threads</a>
          </div>
          <div className="forum-stats">
            <div className="stat-card">
              <span className="stat-label">Kategorien</span>
              <strong>9</strong>
              <p>Von Einstieg bis Rechtliches</p>
            </div>
            <div className="stat-card">
              <span className="stat-label">Technik-Fokus</span>
              <strong>Equipment</strong>
              <p>Beleuchtung, Klima, Automation</p>
            </div>
            <div className="stat-card">
              <span className="stat-label">Hilfebereich</span>
              <strong>SOS</strong>
              <p>Schnelle Antworten bei Problemen</p>
            </div>
          </div>
        </div>
        <div className="forum-search-card">
          <h3>Suche im Forum</h3>
          <p>Finde Threads zu Geräten, Symptomen oder Grow-Strategien.</p>
          <div className="forum-search-input">
            <span aria-hidden="true">🔎</span>
            <input type="text" placeholder="Themen, Geräte oder Probleme suchen" aria-label="Forum durchsuchen" />
            <button type="button">Suchen</button>
          </div>
          <div className="forum-quick-links">
            <span>🌱 Grundlagen</span>
            <span>🔧 Technik</span>
            <span>🛟 Hilfe</span>
            <span>📓 Tagebücher</span>
          </div>
        </div>
      </section>

      <section id="new-thread" className="forum-new-thread" aria-label="Neuen Thread erstellen">
        <div className="forum-new-card">
          <div>
            <p className="forum-kicker">Diskussion starten</p>
            <h2>Eigenen Thread erstellen</h2>
            <p>Teile dein Setup, stelle Fragen oder dokumentiere deine Grow-Erfahrungen.</p>
          </div>
          <div className="forum-new-grid">
            <label className="forum-field">
              <span>Titel</span>
              <input
                type="text"
                value={newThread.title}
                onChange={(e) => setNewThread({ ...newThread, title: e.target.value })}
                placeholder="z. B. Luftfeuchtigkeit stabil halten"
              />
            </label>
            <label className="forum-field">
              <span>Kategorie</span>
              <select
                value={newThread.category}
                onChange={(e) => setNewThread({ ...newThread, category: e.target.value })}
              >
                {categories.map((c) => (
                  <option key={c.title} value={c.title}>{c.title}</option>
                ))}
              </select>
            </label>
            <label className="forum-field full">
              <span>Inhalt</span>
              <ProductMentionEditor
                value={newThread.content}
                onChange={(e) => setNewThread({ ...newThread, content: e })}
                placeholder="Beschreibe dein Problem, Setup oder deine Frage... (Gib # ein um Produkte zu erwähnen)"
              />
            </label>
            <div className="forum-new-actions">
              <button type="button" className="forum-btn primary" onClick={handleCreateThread}>
                Beitrag veröffentlichen
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="forum-grid" aria-label="Forum Kategorien">
        {categories.map((category) => {
          const stats = getCategoryStats(category.title);
          return (
            <article key={category.title} className="forum-card">
              <div className="forum-card-header compact">
                <span className="forum-icon" aria-hidden="true">{category.icon}</span>
                <h2 className="forum-card-title">{category.title}</h2>
              </div>
              <div className="forum-card-meta">
                <span className="chip" title="Anzahl Threads">🧵 {stats.count}</span>
                <span className="chip" title="Jüngste Aktivität">⏱ {stats.latest ? formatRelativeTime(stats.latest) : '–'}</span>
              </div>
              <button type="button" className="forum-card-btn" onClick={() => openCategory(category.title)}>Bereich öffnen</button>
            </article>
          );
        })}
      </section>

      <section id="threads" className="forum-threads" aria-label="Aktuelle Threads">
        <div className="forum-threads-header">
          <div>
            <p className="forum-kicker">Laufende Diskussionen</p>
            <h2>Threads & Antworten</h2>
          </div>
          <select
            className="forum-filter"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Alle Kategorien</option>
            {categories.map((c) => (
              <option key={c.title} value={c.title}>{c.title}</option>
            ))}
          </select>
        </div>

        {error && <div className="forum-error" role="alert">{error}</div>}

        <div className="thread-list">
          {filteredThreads.length === 0 && (
            <div className="thread-empty">Keine Threads in dieser Kategorie. Starte den ersten Beitrag!</div>
          )}

          {filteredThreads.map((thread) => (
            <article key={thread.id} className="thread-card">
              <header className="thread-header">
                <div>
                  <p className="thread-meta">{thread.category}</p>
                  <h3>{thread.title}</h3>
                  <p className="thread-author">
                    {thread.authorProfileImage ? (
                      <img src={thread.authorProfileImage} alt={thread.author} className="thread-author-avatar" />
                    ) : (
                      <span className="thread-author-avatar-placeholder">👤</span>
                    )}
                    Von {thread.author} • {new Date(thread.createdAt).toLocaleString('de-DE')}
                  </p>
                </div>
                {(user && (isAdmin || String(thread.authorId) === String(user.id))) && (
                  <div className="thread-actions">
                    {editingThreadId === thread.id ? (
                      <>
                        <button type="button" className="forum-action-btn" onClick={() => handleSaveThread(thread.id)}>
                          💾 Speichern
                        </button>
                        <button type="button" className="forum-action-btn ghost" onClick={cancelEditThread}>
                          ✕ Abbrechen
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="forum-action-btn" onClick={() => startEditThread(thread)}>
                          ✏️ Bearbeiten
                        </button>
                        <button type="button" className="forum-action-btn danger" onClick={() => handleDeleteThread(thread.id)}>
                          🗑️ Löschen
                        </button>
                      </>
                    )}
                  </div>
                )}
              </header>
              {editingThreadId === thread.id ? (
                <div className="thread-edit-form">
                  <input
                    type="text"
                    className="thread-edit-title"
                    value={editingThreadTitle}
                    onChange={(e) => setEditingThreadTitle(e.target.value)}
                  />
                  <ProductMentionEditor
                    value={editingThreadContent}
                    onChange={(e) => setEditingThreadContent(e)}
                    placeholder="Beitrag bearbeiten..."
                  />
                </div>
              ) : (
                <div className="thread-content">
                  <ProductMentionRenderer
                    text={thread.content}
                  />
                </div>
              )}

              <div className="thread-replies">
                <p className="thread-meta">Antworten ({thread.replies.length})</p>
                {thread.replies.length === 0 && (
                  <div className="reply-empty">Noch keine Antworten. Sei der Erste!</div>
                )}
                {thread.replies.map((reply) => (
                  <div key={reply.id} className="reply-item">
                    {reply.authorProfileImage ? (
                      <img src={reply.authorProfileImage} alt={reply.author} className="reply-avatar-img" />
                    ) : (
                      <div className="reply-avatar" aria-hidden="true">💬</div>
                    )}
                    <div>
                      <p className="reply-meta">{reply.author} • {new Date(reply.createdAt).toLocaleString('de-DE')}</p>
                      {editingReplyId === reply.id ? (
                        <div className="reply-edit-form">
                          <ProductMentionEditor
                            value={editingReplyContent}
                            onChange={(e) => setEditingReplyContent(e)}
                            placeholder="Antwort bearbeiten..."
                          />
                        </div>
                      ) : (
                        <div className="reply-text">
                          <ProductMentionRenderer
                            text={reply.content}
                          />
                        </div>
                      )}
                      {(user && (isAdmin || String(reply.authorId) === String(user.id))) && (
                        <div className="reply-actions">
                          {editingReplyId === reply.id ? (
                            <>
                              <button
                                type="button"
                                className="forum-action-btn"
                                onClick={() => handleSaveReply(thread.id, reply.id)}
                              >
                                💾 Speichern
                              </button>
                              <button
                                type="button"
                                className="forum-action-btn ghost"
                                onClick={cancelEditReply}
                              >
                                ✕ Abbrechen
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="forum-action-btn"
                                onClick={() => startEditReply(reply)}
                              >
                                ✏️ Bearbeiten
                              </button>
                              <button
                                type="button"
                                className="forum-action-btn danger"
                                onClick={() => handleDeleteReply(thread.id, reply.id)}
                              >
                                🗑️ Löschen
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="reply-form">
                <ProductMentionEditor
                  value={replyInputs[thread.id] || ''}
                  onChange={(e) => setReplyInputs({ ...replyInputs, [thread.id]: e })}
                  placeholder="Antwort hinzufügen... (Gib @ ein um Produkte zu erwähnen)"
                />
                <button type="button" className="forum-btn primary" onClick={() => handleAddReply(thread.id)}>
                  Antworten
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="forum-cta">
        <div className="forum-cta-card">
          <div>
            <h3>Starte jetzt dein erstes Thema</h3>
            <p>Teile deine Fragen oder Erfahrungen und hilf der Community, besser zu werden.</p>
          </div>
          <div className="forum-cta-actions">
            <button type="button" className="forum-btn primary">Beitrag erstellen</button>
            <button type="button" className="forum-btn ghost">Community-Richtlinien</button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ForumPage;
